import { PageHeader, LoadingState, DataTable, EmptyState, ActionToolbar } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Box, Button, TextField, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody, MenuItem, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Card, CardContent, Avatar, Stack, Tooltip, Container, FormControl, InputLabel, Select, Collapse, Divider, Paper, useMediaQuery, alpha, useTheme } from '@mui/material';
import {
  Edit, Delete, Class, PersonAdd, Group, Subject,
  Phone, Email, ExpandMore, ExpandLess, Settings,
  CheckCircle, Cancel, Visibility, School, PhonelinkErase,
  Male, Female, Event, Badge, Place, AccountBalance,
  Assignment, Today, CloudUpload
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import api from '../utils/Api';
import TeacherForm from '../components/TeacherForm';
import BulkTeacherModal from '../components/BulkTeacherModal';
import { hasPermission } from '../utils/permissions';

const formatPhone = (phone) => {
  if (!phone) return 'No phone';
  const str = String(phone).trim();
  if (str.startsWith('0') || str.startsWith('+')) return str;
  return `0${str}`;
};

export default function TeachersPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // State declarations
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const canViewTeachers = hasPermission('canViewTeachers', currentUser);
  const canAddTeachers = hasPermission('canAddTeachers', currentUser);
  const canEditTeachers = hasPermission('canEditTeachers', currentUser);
  const canDeleteTeachers = hasPermission('canDeleteTeachers', currentUser);
  const isGovernment = currentUser.school?.schoolType === 'Government';
  const [assigningClassTeacher, setAssigningClassTeacher] = useState(null);
  const [selectedClassForAssignment, setSelectedClassForAssignment] = useState('');
  const [loading, setLoading] = useState(false); // For local actions like assign/delete
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [showTeacherForm, setShowTeacherForm] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);
  const [teacherToResetDevice, setTeacherToResetDevice] = useState(null);
  const [showResetConfirmDialog, setShowResetConfirmDialog] = useState(false);
  const [viewingParticulars, setViewingParticulars] = useState(null);
  const [isCoTeacherAssignment, setIsCoTeacherAssignment] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);


  // Helper functions
  const getTeacherCurrentClassTeacherPosition = (teacherId) => {
    const primaryClass = classes.find(c => c.classTeacher?._id === teacherId);
    if (primaryClass) return { class: primaryClass, role: 'primary' };

    const coTeacherClass = classes.find(c => c.coClassTeacher?._id === teacherId);
    if (coTeacherClass) return { class: coTeacherClass, role: 'co' };

    return null;
  };

  const getInitials = (name) =>
    name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'T';

  // Fetch data
  const fetchTeachers = useCallback(async () => {
    try {
      const res = await api.get('/teachers');
      const data = Array.isArray(res.data?.teachers) ? res.data.teachers : [];
      setTeachers(data.sort((a, b) => (a.user?.name || '').localeCompare(b.user?.name || '')));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load teachers');
    }
  }, []);

  const fetchClasses = useCallback(async () => {
    try {
      const res = await api.get('/classes');
      setClasses(Array.isArray(res.data?.classes) ? res.data.classes : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load classes');
    }
  }, []);

  const fetchSubjects = useCallback(async () => {
    try {
      const res = await api.get('/subjects');
      let data = res.data?.subjects ?? res.data ?? [];

      if (!Array.isArray(data) || data.length === 0) {
        const fallback = await api.get('/subjects/global/list');
        data = fallback.data?.subjects ?? fallback.data ?? [];
      }

      setSubjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch subjects:', err);
      toast.error('Failed to load subjects');
      setSubjects([]);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetching(true);
        await Promise.all([fetchTeachers(), fetchClasses(), fetchSubjects()]);
      } catch {
        setError('Failed to load data');
        toast.error('Failed to load data');
      } finally {
        setFetching(false);
      }
    };
    fetchData();
  }, [fetchTeachers, fetchClasses, fetchSubjects]);

  // Handlers
  const handleEdit = useCallback((teacher) => {
    if (!canEditTeachers) {
      toast.error('You are not allowed to perform this action');
      return;
    }
    setEditingTeacher(teacher);
    setShowTeacherForm(true);
  }, [canEditTeachers]);

  const handleFormSuccess = useCallback(() => {
    fetchTeachers();
    setEditingTeacher(null);
    setShowTeacherForm(false);
  }, [fetchTeachers]);

  const handleFormCancel = useCallback(() => {
    setEditingTeacher(null);
    setShowTeacherForm(false); // Optional: close form on cancel
  }, []);

  const handleDeleteClick = useCallback((teacher) => {
    if (!canDeleteTeachers) {
      toast.error('You are not allowed to perform this action');
      return;
    }
    setTeacherToDelete(teacher);
    setShowConfirmDialog(true);
  }, [canDeleteTeachers]);

  const handleDeleteConfirm = async () => {
    if (!teacherToDelete) return;

    try {
      await api.delete(`/teachers/${teacherToDelete._id}`);
      toast.success('Teacher deleted successfully');
      fetchTeachers();
    } catch {
      toast.error('Failed to delete teacher');
    } finally {
      setShowConfirmDialog(false);
      setTeacherToDelete(null);
    }
  };

  // Helper to scroll to element
  const scrollToElement = (id) => {
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.style.transition = 'background-color 2s';
        element.style.backgroundColor = alpha(theme.palette.primary.main, 0.2);
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 2000);
      }
    }, 500);
  };

  useEffect(() => {
    if (!fetching && teachers.length > 0) {
      const params = new URLSearchParams(window.location.search);
      const searchTerm = params.get('search'); // Currently HomePage doesn't send search for teachers, just navigation. But let's support it. 
      // Wait, HomePage sends this for Students. For teachers it just navigates to /dashboard/teachers.
      // But if we improved HomePage to send ?search=${id} or name, we can support it.
      // The user request implies searching and clicking result.

      // If the HomePage sends IDs or unique props we can use that.
      // Currently HomePage sends: navigate('/dashboard/teachers'); without params for teachers.
      // I should update HomePage to send params for Teachers and Classes too.

      if (searchTerm) {
        // Find teacher by email or name
        const target = teachers.find(t =>
          t.email === searchTerm ||
          t.user?.email === searchTerm ||
          t.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t._id === searchTerm
        );

        if (target) {
          scrollToElement(target._id);
        }
      }
    }
  }, [fetching, teachers, theme.palette.primary.main]);

  const handleResetDeviceClick = useCallback((teacher) => {
    if (!canEditTeachers) {
      toast.error('You are not allowed to perform this action');
      return;
    }
    setTeacherToResetDevice(teacher);
    setShowResetConfirmDialog(true);
  }, [canEditTeachers]);

  const handleResetDeviceConfirm = async () => {
    if (!teacherToResetDevice) return;

    try {
      await api.post('/device-binding/reset', { teacherId: teacherToResetDevice._id });
      toast.success('Device binding reset successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to reset device binding');
    } finally {
      setShowResetConfirmDialog(false);
      setTeacherToResetDevice(null);
    }
  };

  const handleAssignClassTeacher = useCallback((teacher) => {
    if (!canEditTeachers) {
      toast.error('You are not allowed to perform this action');
      return;
    }
    setAssigningClassTeacher(teacher);
    setSelectedClassForAssignment('');
    setIsCoTeacherAssignment(false); // Reset to primary by default
  }, [canEditTeachers]);

  const handleAssignClassTeacherSubmit = async () => {
    if (!selectedClassForAssignment) return toast.error('Select a class');

    setLoading(true);
    try {
      // Use the User ID (from the populated object or string ID)
      const teacherIdToSend = (typeof assigningClassTeacher.user === 'object') 
        ? assigningClassTeacher.user?._id 
        : (assigningClassTeacher.user || assigningClassTeacher._id);

      const res = await api.post(
        `/classes/${selectedClassForAssignment}/assign-class-teacher`,
        { teacherId: teacherIdToSend, isCoTeacher: isCoTeacherAssignment }
      );

      if (res.data?.success) {
        toast.success('Class teacher assigned successfully');
        setAssigningClassTeacher(null);
        setSelectedClassForAssignment('');
        fetchTeachers();
        fetchClasses();
      } else {
        throw new Error(res.data?.message || 'Unexpected error');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.message || 'Failed to assign class teacher');
    } finally {
      setLoading(false);
    }
  };



  const teacherClassMap = useMemo(() => {
    const map = {};
    if (!classes.length || !teachers.length) return map;

    teachers.forEach(t => {
      const tId = t.user?._id || t._id;
      if (tId) {
        map[tId] = classes.filter(c => c.classTeacher?._id === tId || c.coClassTeacher?._id === tId).map(c => ({
          ...c,
          isCoTeacher: c.coClassTeacher?._id === tId
        }));
      }
    });
    return map;
  }, [classes, teachers]);

  const getClassTeacherInfo = (teacher) => {
    const tId = teacher.user?._id || teacher._id;
    return teacherClassMap[tId] || [];
  };

  // Stats
  const totalTeachers = teachers.length;
  const classTeachersCount = classes.filter(cls => cls.classTeacher).length;
  const maleTeachers = teachers.filter(t => ['Male', 'male'].includes(t.gender || t.user?.gender)).length;
  const femaleTeachers = teachers.filter(t => ['Female', 'female'].includes(t.gender || t.user?.gender)).length;

  if (fetching) {
    return (
      <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
        <LoadingState />
      </Container>
    );
  }

  if (!canViewTeachers) {
    return (
      <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
        <Alert severity="error">You are not allowed to perform this action</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>Teacher Management</>} description={<>Manage teachers, subjects, and class assignments</>} actions={<>
        {canAddTeachers && <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => setShowBulkModal(true)} fullWidth={isMobile}>
                    Import CSV
                  </Button>}
        {canAddTeachers && !showTeacherForm && <Button variant="contained" color="primary" startIcon={<PersonAdd />} onClick={() => setShowTeacherForm(true)} fullWidth={isMobile}>
                    Add Teacher
                  </Button>}
      </>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 6, md: 4 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.primary.main}`, transition: 'all 0.3s ease', '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <Group fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}>
                    Total Teachers
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}>
                    {totalTeachers}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                    Teaching staff
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 4 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.info.main}`, transition: 'all 0.3s ease', '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.info.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <Male fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}>
                    Male Teachers
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}>
                    {maleTeachers}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                    Total male staff
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 4 }}>
          <SummaryMetric sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.secondary.main}`, transition: 'all 0.3s ease', '&:hover': { transform: isMobile ? 'none' : 'translateY(-4px)', boxShadow: isMobile ? 2 : 6 } }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: isMobile ? 36 : 48, height: isMobile ? 36 : 48 }}>
                  <Female fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "caption" : "h6"} color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '1rem' }}>
                    Female Teachers
                  </Typography>
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ fontSize: isMobile ? '1.25rem' : '2.125rem' }}>
                    {femaleTeachers}
                  </Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                    Total female staff
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>

      {/* Stats Cards */}
      

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Teacher Form */}
      {(canAddTeachers || canEditTeachers) && (
        <TeacherForm
          open={showTeacherForm}
          onToggle={() => setShowTeacherForm(prev => !prev)}
          editingTeacher={editingTeacher}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
          subjects={subjects}
          classes={classes}
        />
      )}

      {/* Bulk Import Modal */}
      {canAddTeachers && (
        <BulkTeacherModal
          open={showBulkModal}
          onClose={() => setShowBulkModal(false)}
          onSuccess={() => {
            fetchTeachers();
            setShowBulkModal(false);
          }}
        />
      )}

      {/* Teachers Table / List */}
      <Card sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: isMobile ? 2 : 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 1 : 0}>
              <Typography variant="h6" fontWeight="600" sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                Teaching Staff ({teachers.length})
              </Typography>
              {teachers.length > 0 && !isMobile && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip label={`${totalTeachers} teachers`} color="primary" variant="outlined" />
                  <Chip label={`${classTeachersCount} class teachers`} color="success" variant="outlined" />
                </Stack>
              )}
            </Stack>
          </Box>

          {teachers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <Group sx={{ fontSize: isMobile ? 48 : 64, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>No teachers found</Typography>
              <Typography variant="body2" color="text.secondary">Add your first teacher to get started</Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ p: 2 }}>
              <Stack spacing={1}>
                {teachers.length === 0 && <EmptyState title="No teachers found" description="Try another search or add a new record using the actions above." />}
{teachers.map((teacher) => (
                  <MobileTeacherCard
                    key={teacher._id}
                    teacher={teacher}
                    theme={theme}
                    isGovernment={isGovernment}
                    handleEdit={handleEdit}
                    handleDeleteClick={handleDeleteClick}
                    handleAssignClassTeacher={handleAssignClassTeacher}
                    handleResetDeviceClick={handleResetDeviceClick}
                    setViewingParticulars={setViewingParticulars}
                    classTeacherFor={getClassTeacherInfo(teacher)}
                    canEditTeachers={canEditTeachers}
                    canDeleteTeachers={canDeleteTeachers}
                  />
                ))}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ width: '100%', overflow: 'auto', maxHeight: isTablet ? '60vh' : 'none' }}>
              <DataTable><Table sx={{ minWidth: isTablet ? 1000 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TEACHER</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CONTACT</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>SUBJECTS</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CLASSES</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CLASS TEACHER</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
{teachers.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                  {teachers.map((teacher) => (
                    <TableRow
                      key={teacher._id}
                      id={teacher._id}
                      sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) }, transition: 'background-color 0.5s' }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }} src={teacher.user?.profilePicture || teacher.profilePicture}>
                            {getInitials(teacher.user?.name || teacher.name)}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight="600"
                              sx={{
                                cursor: isGovernment ? 'pointer' : 'default',
                                color: isGovernment ? theme.palette.primary.main : 'text.primary',
                                '&:hover': { textDecoration: isGovernment ? 'underline' : 'none' }
                              }}
                              onClick={() => isGovernment && setViewingParticulars(teacher)}
                            >
                              {teacher.user?.name || teacher.name}
                            </Typography>
                            {(teacher.ghanaCard || teacher.ntcNumber) && isGovernment && (
                              <Typography variant="body2" color="text.secondary">
                                {teacher.ghanaCard ? `Ghana Card: ${teacher.ghanaCard}` : `NTC: ${teacher.ntcNumber}`}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Email fontSize="small" />{teacher.user?.email || teacher.email}</Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Phone fontSize="small" />{formatPhone(teacher.phone)}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {(teacher.subjects || []).map(sub => (
                            <Chip key={sub._id} label={sub.name} size="small" color="primary" variant="outlined" />
                          ))}
                          {(teacher.subjects || []).length === 0 && <Typography variant="caption" color="text.secondary">No subjects</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack spacing={0.5}>
                          {(teacher.assignedClasses || []).slice(0, 2).map(cls => (
                            <Chip key={cls._id} label={cls.displayName || cls.name} size="small" variant="outlined" />
                          ))}
                          {(teacher.assignedClasses || []).length > 2 && <Typography variant="caption" color="text.secondary">+{(teacher.assignedClasses || []).length - 2} more</Typography>}
                          {(teacher.assignedClasses || []).length === 0 && <Typography variant="caption" color="text.secondary">No classes</Typography>}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {getClassTeacherInfo(teacher).length > 0 ? (
                          <Stack spacing={0.5}>
                            {getClassTeacherInfo(teacher).map(cls => (
                              <Chip 
                                key={cls._id} 
                                label={`${cls.displayName || cls.name} ${cls.isCoTeacher ? '(Co-Teacher)' : ''}`} 
                                size="small" 
                                color={cls.isCoTeacher ? "info" : "success"} 
                                icon={<Class />} 
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Not assigned</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2, textAlign: 'center' }}>
                        <Stack direction="row" spacing={1} justifyContent="center">
                          {canEditTeachers && (
                            <Tooltip title="Assign Class Teacher">
                              <IconButton onClick={() => handleAssignClassTeacher(teacher)} color="info" size="small">
                                <Class fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canEditTeachers && (
                            <Tooltip title="Edit Teacher">
                              <IconButton onClick={() => handleEdit(teacher)} color="primary" size="small">
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDeleteTeachers && (
                            <Tooltip title="Delete Teacher">
                              <IconButton onClick={() => handleDeleteClick(teacher)} color="error" size="small">
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canEditTeachers && (
                            <Tooltip title="Reset Device">
                              <IconButton onClick={() => handleResetDeviceClick(teacher)} color="warning" size="small">
                                <PhonelinkErase fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></DataTable>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <Dialog open={!!assigningClassTeacher} onClose={() => setAssigningClassTeacher(null)} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, m: isMobile ? 0 : 'auto' } }}>
        <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Class /> Assign Class Teacher
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3, mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Assign <strong>{assigningClassTeacher?.user?.name || assigningClassTeacher?.name}</strong> as class teacher for:
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Select 
              value={isCoTeacherAssignment ? 'co' : 'primary'} 
              onChange={(e) => setIsCoTeacherAssignment(e.target.value === 'co')} 
              size={isMobile ? "small" : "medium"}
              sx={{ mb: 1 }}
            >
              <MenuItem value="primary">Primary Class Teacher</MenuItem>
              <MenuItem value="co">Co-Class Teacher</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel>Select Class</InputLabel>
            <Select value={selectedClassForAssignment} onChange={(e) => setSelectedClassForAssignment(e.target.value)} label="Select Class">
              <MenuItem value="none" sx={{ color: 'error.main' }}>Remove Assignment (Unassign)</MenuItem>
              {classes.map(cls => <MenuItem key={cls._id} value={cls._id}>{cls.displayName || cls.name}</MenuItem>)}
            </Select>
          </FormControl>
          {selectedClassForAssignment && (() => {
            const teacherId = assigningClassTeacher?.user?._id || assigningClassTeacher?._id;
            
            if (selectedClassForAssignment === 'none') {
              return <Alert severity="warning" sx={{ mt: 2 }}>This will REMOVE the teacher from the {isCoTeacherAssignment ? 'Co-Class Teacher' : 'Primary Class Teacher'} role in ALL classes.</Alert>;
            }

            const newClass = classes.find(c => c._id === selectedClassForAssignment);
            
            // Check if they are already assigned to THIS specific class in the selected role
            const isAlreadyAssignedHere = (isCoTeacherAssignment ? newClass?.coClassTeacher?._id : newClass?.classTeacher?._id) === teacherId;

            if (isAlreadyAssignedHere) {
              return <Alert severity="warning" sx={{ mt: 2 }}>Teacher is already {isCoTeacherAssignment ? 'Co-Class Teacher' : 'Primary class teacher'} for <strong>{newClass?.displayName || newClass?.name}</strong>.</Alert>;
            }
            
            return <Alert severity="info" sx={{ mt: 2 }}>Assigning to <strong>{newClass?.displayName || newClass?.name}</strong> as {isCoTeacherAssignment ? 'Co-Class Teacher' : 'Primary Class Teacher'}</Alert>;
          })()}
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
          <Button onClick={() => setAssigningClassTeacher(null)} variant="outlined" fullWidth={isMobile}>Cancel</Button>
          <Button onClick={handleAssignClassTeacherSubmit} variant="contained" disabled={!selectedClassForAssignment || loading} fullWidth={isMobile}>
            {loading ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, m: isMobile ? 0 : 'auto' } }}>
        <DialogTitle sx={{ bgcolor: theme.palette.error.main, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete /> Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3, mt: 2 }}>
          <Typography>Are you sure you want to delete teacher <strong>{teacherToDelete?.user?.name || teacherToDelete?.name}</strong>?</Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>Action cannot be undone.</Alert>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
          <Button onClick={() => setShowConfirmDialog(false)} variant="outlined">Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showResetConfirmDialog} onClose={() => setShowResetConfirmDialog(false)} fullScreen={isMobile} PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, m: isMobile ? 0 : 'auto' } }}>
        <DialogTitle sx={{ bgcolor: theme.palette.warning.main, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhonelinkErase /> Confirm Device Reset
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3, mt: 2 }}>
          <Typography>Reset device binding for <strong>{teacherToResetDevice?.user?.name || teacherToResetDevice?.name}</strong>?</Typography>
          <Alert severity="info" sx={{ mt: 2 }}>Allows login from new device.</Alert>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
          <Button onClick={() => setShowResetConfirmDialog(false)} variant="outlined">Cancel</Button>
          <Button onClick={handleResetDeviceConfirm} variant="contained" color="warning">Reset</Button>
        </DialogActions>
      </Dialog>

      {/* View Particulars Dialog */}
      <Dialog
        open={!!viewingParticulars}
        onClose={() => setViewingParticulars(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <School /> Teacher Particulars: {viewingParticulars?.user?.name || viewingParticulars?.name}
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 4 }}>
          {viewingParticulars && (
            <Stack spacing={4} sx={{ mt: 1 }}>
              {[
                {
                  title: 'Basic Information',
                  icon: <School color="primary" />,
                  fields: [
                    { label: 'Staff ID', value: viewingParticulars.staffId },
                    { label: 'Reg No', value: viewingParticulars.regNo },
                    { label: 'Rank', value: viewingParticulars.rank },
                    { label: 'Present Rank', value: viewingParticulars.presentRank },
                    { label: 'Academic Qualification', value: viewingParticulars.academicQualification },
                    { label: 'Professional Qualification', value: viewingParticulars.professional },
                    { label: 'Ghana Card', value: viewingParticulars.ghanaCard },
                    { label: 'NTC Number', value: viewingParticulars.ntcNumber },
                    { label: 'Class Taught', value: viewingParticulars.classTaught },
                  ]
                },
                {
                  title: 'Experience & Dates',
                  icon: <Event color="primary" />,
                  fields: [
                    { label: 'Teaching Experience', value: viewingParticulars.teachingExperience },
                    { label: 'Previous School Taught', value: viewingParticulars.previousSchoolTaught },
                    { label: 'Institution Attended', value: viewingParticulars.institutionAttended },
                    { label: 'Year of Certification', value: viewingParticulars.yearOfCertification },
                    { label: 'Date of 1st Appointment', value: viewingParticulars.dateOfFirstAppointment ? new Date(viewingParticulars.dateOfFirstAppointment).toLocaleDateString() : '' },
                    { label: 'Date Promoted to Present Rank', value: viewingParticulars.datePromotedToPresentRank ? new Date(viewingParticulars.datePromotedToPresentRank).toLocaleDateString() : '' },
                    { label: 'Date Posted to Present Station', value: viewingParticulars.datePostedToPresentStation ? new Date(viewingParticulars.datePostedToPresentStation).toLocaleDateString() : '' },
                    { label: 'Expected Date of Retirement', value: viewingParticulars.expectedDateOfRetirement ? new Date(viewingParticulars.expectedDateOfRetirement).toLocaleDateString() : '' },
                  ]
                },
                {
                  title: 'Personal Details',
                  icon: <Badge color="primary" />,
                  fields: [
                    { label: 'Date of Birth', value: viewingParticulars.dateOfBirth ? new Date(viewingParticulars.dateOfBirth).toLocaleDateString() : '' },
                    { label: 'Sex', value: viewingParticulars.sex },
                    { label: 'Place of Birth', value: viewingParticulars.placeOfBirth },
                    { label: 'Nationality', value: viewingParticulars.nationality },
                    { label: 'Marital Status', value: viewingParticulars.maritalStatus },
                    { label: 'Language Spoken', value: viewingParticulars.languageSpoken },
                    { label: 'Religious Denomination', value: viewingParticulars.religiousDenomination },
                    { label: 'Next of Kin', value: viewingParticulars.nextOfKin },
                  ]
                },
                {
                  title: 'Contact & Location',
                  icon: <Place color="primary" />,
                  fields: [
                    { label: 'Alt Phone / Tel No', value: viewingParticulars.telNo },
                    { label: 'Residential Address', value: viewingParticulars.residentialAddress },
                    { label: 'Digital Address', value: viewingParticulars.digitalAddress },
                    { label: 'Hometown', value: viewingParticulars.hometown },
                    { label: 'District', value: viewingParticulars.district },
                    { label: 'Region', value: viewingParticulars.region },
                  ]
                },
                {
                  title: 'Financial & Statutory',
                  icon: <AccountBalance color="primary" />,
                  fields: [
                    { label: 'Bank Name', value: viewingParticulars.bank },
                    { label: 'Bank Account Number', value: viewingParticulars.bankAccount },
                    { label: 'SSNIT Number', value: viewingParticulars.ssnitNumber },
                    { label: 'TIN Number', value: viewingParticulars.tinNumber },
                  ]
                }
              ].map((section, sIdx) => (
                <Box key={sIdx}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    {section.icon}
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {section.title}
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {section.fields.map((field, fIdx) => field.value ? (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}     key={fIdx}>
                        <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.03), borderRadius: 1, height: '100%', border: `1px solid ${alpha(theme.palette.primary.main, 0.05)}` }}>
                          <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold', display: 'block', lineHeight: 1.2, mb: 0.5 }}>
                            {field.label}
                          </Typography>
                          <Typography variant="body1" color="text.primary" sx={{ fontWeight: '600', wordBreak: 'break-word' }}>
                            {field.value || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                    ) : null)}
                  </Grid>
                  {sIdx < 4 && <Divider sx={{ mt: 4, opacity: 0.3 }} />}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 3 }}>
          <Button onClick={() => setViewingParticulars(null)} variant="outlined" fullWidth>Close</Button>
        </DialogActions>
      </Dialog>


    </Container>
  );
}

// Mobile Card outside component
const MobileTeacherCard = React.memo(({
  teacher,
  theme,
  isGovernment,
  classTeacherFor,
  handleEdit,
  handleDeleteClick,
  handleAssignClassTeacher,
  handleResetDeviceClick,
  setViewingParticulars,
  canEditTeachers,
  canDeleteTeachers,
}) => {
  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'T';
  return (
    <Card sx={{ mb: 2, boxShadow: 2, transition: 'background-color 0.5s' }} id={teacher._id}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }} src={teacher.user?.profilePicture || teacher.profilePicture}>{getInitials(teacher.user?.name || teacher.name)}</Avatar>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  sx={{ 
                    color: isGovernment ? theme.palette.primary.main : 'text.primary', 
                    textDecoration: isGovernment ? 'underline' : 'none',
                    cursor: isGovernment ? 'pointer' : 'default'
                  }}
                  onClick={() => isGovernment && setViewingParticulars(teacher)}
                >
                  {teacher.user?.name || teacher.name}
                </Typography>
                {(teacher.ghanaCard || teacher.ntcNumber) && isGovernment && (
                  <Typography variant="caption" color="text.secondary">
                    {teacher.ghanaCard || teacher.ntcNumber}
                  </Typography>
                )}
              </Box>
            </Stack>
            {classTeacherFor.length > 0 && <Chip label="Class Teacher" size="small" color="success" icon={<Class />} />}
          </Stack>
          <Stack spacing={1}>
            <Box display="flex" alignItems="center" gap={1}><Email fontSize="small" color="action" /><Typography variant="body2" noWrap>{teacher.user?.email || teacher.email}</Typography></Box>
            <Box display="flex" alignItems="center" gap={1}><Phone fontSize="small" color="action" /><Typography variant="body2">{formatPhone(teacher.phone)}</Typography></Box>
          </Stack>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>SUBJECTS</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {(teacher.subjects || []).slice(0, 3).map(sub => <Chip key={sub._id} label={sub.name} size="small" color="primary" variant="outlined" sx={{ mb: 0.5 }} />)}
              {(teacher.subjects || []).length > 3 && <Chip label={`+${(teacher.subjects || []).length - 3} more`} size="small" variant="outlined" sx={{ mb: 0.5 }} />}
              {(teacher.subjects || []).length === 0 && <Typography variant="caption" color="text.secondary">No subjects</Typography>}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>CLASSES</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {(teacher.assignedClasses || []).slice(0, 3).map(cls => <Chip key={cls._id} label={cls.displayName || cls.name} size="small" variant="outlined" sx={{ mb: 0.5 }} />)}
              {(teacher.assignedClasses || []).length > 3 && <Chip label={`+${(teacher.assignedClasses || []).length - 3} more`} size="small" variant="outlined" sx={{ mb: 0.5 }} />}
              {(teacher.assignedClasses || []).length === 0 && <Typography variant="caption" color="text.secondary">No classes</Typography>}
            </Stack>
          </Box>
          {classTeacherFor.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>CLASS TEACHER FOR</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {classTeacherFor.map(cls => <Chip key={cls._id} label={cls.displayName || cls.name} size="small" color="success" sx={{ mb: 0.5 }} />)}
              </Stack>
            </Box>
          )}
          {(canEditTeachers || canDeleteTeachers) && (
            <ActionToolbar direction="row" spacing={1} justifyContent="space-between">
              {canEditTeachers && (
                <Button variant="outlined" size="small" onClick={() => handleAssignClassTeacher(teacher)} sx={{ flex: 1 }}>Assign</Button>
              )}
              {canEditTeachers && (
                <Button variant="outlined" size="small" onClick={() => handleEdit(teacher)} sx={{ flex: 1 }}>Edit</Button>
              )}
              {canDeleteTeachers && (
                <Button variant="outlined" size="small" color="error" onClick={() => handleDeleteClick(teacher)} sx={{ flex: 1 }}>Delete</Button>
              )}
            </ActionToolbar>
          )}
          {canEditTeachers && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button variant="outlined" size="small" color="warning" fullWidth onClick={() => handleResetDeviceClick(teacher)}>Reset Device</Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
});
